class FokusHeroFlavorSlider extends HTMLElement {
  #order = [];
  #autoplayTimer = null;
  #autoplayDelay = 5000;

  connectedCallback() {
    this.pouches = Array.from(
      this.querySelectorAll('[data-hero-pouch]')
    );

    this.panels = Array.from(
      this.querySelectorAll('[data-hero-panel]')
    );

    this.dots = Array.from(
      this.querySelectorAll('[data-hero-dot]')
    );

    this.#order = this.pouches.map(
      (pouch) => pouch.dataset.heroPouch
    );

    if (this.#order.length < 2) return;

    this.querySelector('[data-hero-prev]')?.addEventListener(
      'click',
      () => {
        this.#step(-1);
        this.#restartAutoplay();
      }
    );

    this.querySelector('[data-hero-next]')?.addEventListener(
      'click',
      () => {
        this.#step(1);
        this.#restartAutoplay();
      }
    );

    for (const dot of this.dots) {
      dot.addEventListener('click', () => {
        this.#setFlavor(dot.dataset.heroDot);
        this.#restartAutoplay();
      });
    }

    /* Pause when the customer hovers over the hero */
    this.addEventListener('mouseenter', () => {
      this.#stopAutoplay();
    });

    this.addEventListener('mouseleave', () => {
      this.#startAutoplay();
    });

    /* Pause while someone is using the controls */
    this.addEventListener('focusin', () => {
      this.#stopAutoplay();
    });

    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.#startAutoplay();
      }
    });

    /* Pause autoplay when the browser tab is inactive */
    this.handleVisibilityChange = () => {
      if (document.hidden) {
        this.#stopAutoplay();
      } else {
        this.#startAutoplay();
      }
    };

    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    this.#startAutoplay();
  }

  disconnectedCallback() {
    this.#stopAutoplay();

    if (this.handleVisibilityChange) {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
  }

  #startAutoplay() {
    if (
      this.#order.length < 2 ||
      document.hidden ||
      this.#autoplayTimer
    ) {
      return;
    }

    this.#autoplayTimer = window.setInterval(() => {
      this.#step(1);
    }, this.#autoplayDelay);
  }

  #stopAutoplay() {
    if (!this.#autoplayTimer) return;

    window.clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = null;
  }

  #restartAutoplay() {
    this.#stopAutoplay();
    this.#startAutoplay();
  }

  #step(delta) {
    const current = this.dataset.flavor;
    const currentIndex = this.#order.indexOf(current);

    const safeIndex = currentIndex >= 0 ? currentIndex : 0;

    const nextIndex =
      (safeIndex + delta + this.#order.length) %
      this.#order.length;

    this.#setFlavor(this.#order[nextIndex]);
  }

  #setFlavor(flavor) {
    if (!flavor || flavor === this.dataset.flavor) return;

    this.dataset.flavor = flavor;

    for (const pouch of this.pouches) {
      const isActive =
        pouch.dataset.heroPouch === flavor;

      if (isActive) {
        pouch.hidden = false;
        pouch.classList.add('is-active');

        /* Restart the drop animation */
        pouch.classList.remove('is-dropping');
        void pouch.offsetWidth;
        pouch.classList.add('is-dropping');

        pouch.addEventListener(
          'animationend',
          () => {
            pouch.classList.remove('is-dropping');
          },
          { once: true }
        );
      } else {
        pouch.hidden = true;
        pouch.classList.remove(
          'is-active',
          'is-dropping'
        );
      }
    }

    for (const panel of this.panels) {
      const isActive =
        panel.dataset.heroPanel === flavor;

      panel.hidden = !isActive;
      panel.classList.toggle(
        'is-active',
        isActive
      );
    }

    for (const dot of this.dots) {
      const isActive =
        dot.dataset.heroDot === flavor;

      dot.classList.toggle(
        'is-active',
        isActive
      );

      dot.setAttribute(
        'aria-selected',
        String(isActive)
      );
    }
  }
}

if (
  !customElements.get(
    'fokus-hero-flavor-slider'
  )
) {
  customElements.define(
    'fokus-hero-flavor-slider',
    FokusHeroFlavorSlider
  );
}